/**
 * =============================================================================
 * Seleção por escopo — atalhos de exportação de template
 *
 * Monta a seleção (`TemplateSelection`) do assistente de exportação já
 * filtrada para UM item específico, preservando a praticidade dos antigos
 * botões "Exportar fluxo" e "Exportar workspace".
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-08 v1.2.0 - O catálogo de variáveis deixa de ser recortado
 *            pelas referências encontradas no fluxo: vão TODAS as variáveis
 *            personalizadas da conta (as de sistema nunca são exportadas). Sem
 *            isso, variáveis usadas de outras formas (texto {{nome}} ou campos
 *            que não viravam referência) ficavam de fora e o passo chegava no
 *            destino apontando para variável inexistente.
 *   RELEASE: 2026-09-08 v1.1.0 - Corrigidos dois problemas vistos em teste real:
 *            (1) o fluxo saía sem as variáveis que ele usa (catálogo era zerado);
 *            (2) itens de outras áreas de trabalho com o mesmo nome entravam no
 *            pacote — agora a seleção envia também um escopo (`_scope`) que o
 *            servidor usa para conferir o vínculo do item.
 *   RELEASE: 2026-09-08 v1.0.0 - Versão inicial: seleção de um fluxo e de uma
 *            workspace (com tudo que está vinculado a ela).
 * =============================================================================
 */
import type { TemplateCollectionItems, TemplateSelection } from './api';

/** Mapa de coleções por módulo, como devolvido pelo hook em lote. */
export type ItemsByModule = Record<string, TemplateCollectionItems[]>;

/** Compara nomes ignorando maiúsculas/minúsculas e espaços nas pontas. */
function mesmoNome(a: unknown, b: string): boolean {
  return typeof a === 'string' && a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Lê um campo do item bruto. */
function texto(item: unknown, campo: string): unknown {
  if (!item || typeof item !== 'object') return undefined;
  return (item as Record<string, unknown>)[campo];
}

/** Verifica se `workspace_refs` (array de nomes) contém a workspace. */
function contemWorkspace(item: unknown, workspace: string): boolean {
  const refs = texto(item, 'workspace_refs');
  return Array.isArray(refs) && refs.some((r) => mesmoNome(r, workspace));
}

/**
 * Percorre uma coleção e devolve os rótulos cujos itens passam no teste.
 * Usa o índice para casar item bruto ↔ rótulo exibido no seletor.
 */
function rotulosFiltrados(
  colecao: TemplateCollectionItems | undefined,
  aceita: (item: unknown) => boolean,
): string[] {
  if (!colecao) return [];
  const brutos = colecao.items ?? [];
  return colecao.labels.filter((_, i) => aceita(brutos[i]));
}

/** Localiza uma coleção pelo nome interno dentro do módulo. */
function pegar(
  colecoes: TemplateCollectionItems[] | undefined,
  nome: string,
): TemplateCollectionItems | undefined {
  return (colecoes ?? []).find((c) => c.collection === nome);
}

/**
 * Seleção para exportar UM fluxo.
 * Além do fluxo, leva TODAS as variáveis personalizadas da conta: elas são
 * indispensáveis para o fluxo continuar funcionando na outra conta e nem sempre
 * aparecem como referência (podem estar escritas como {{nome}} dentro de textos).
 * As variáveis globais do sistema nunca entram (já existem em toda conta).
 */
export function buildFlowSelection(
  flowName: string,
  itemsByModule: ItemsByModule,
): TemplateSelection {
  const colecoes = itemsByModule.flows ?? [];
  const selecao: Record<string, string[] | null> = {};

  for (const colecao of colecoes) {
    if (colecao.collection === 'flows') {
      selecao.flows = rotulosFiltrados(colecao, (item) => mesmoNome(texto(item, 'name'), flowName));
      continue;
    }

    if (colecao.collection === 'variables_catalog') {
      // null = "levar tudo" (somente variáveis personalizadas já vêm do servidor)
      selecao.variables_catalog = null;
      continue;
    }

    selecao[colecao.collection] = [];
  }

  // Se o fluxo não apareceu na lista, usa o nome puro como fallback
  if (!selecao.flows || (selecao.flows as string[]).length === 0) selecao.flows = [flowName];

  return { flows: selecao };
}

/**
 * Seleção para exportar UMA área de trabalho, com tudo que está vinculado a ela:
 * funis, fases, categorias, produtos/serviços, tabelas de preço (e seus itens) e
 * playbooks. O escopo (`_scope`) garante que itens homônimos de outras áreas de
 * trabalho não entrem no pacote.
 */
export function buildWorkspaceSelection(
  workspaceName: string,
  itemsByModule: ItemsByModule,
): TemplateSelection {
  const colecoes = itemsByModule.crm ?? [];
  const selecao: Record<string, string[] | null | Record<string, string>> = {
    // Conferência feita no servidor: o item só entra se pertencer a esta área
    _scope: { workspace_ref: workspaceName, workspace_refs: workspaceName },
  };

  const porRefDireta = ['pipelines', 'phases', 'playbooks', 'playbook_steps', 'playbook_bindings'];
  const porRefMultipla = ['product_categories', 'products', 'price_tables'];

  for (const colecao of colecoes) {
    const nome = colecao.collection;

    if (nome === 'workspaces') {
      const encontrados = rotulosFiltrados(colecao, (item) =>
        mesmoNome(texto(item, 'name'), workspaceName),
      );
      selecao[nome] = encontrados.length > 0 ? encontrados : [workspaceName];
      continue;
    }

    if (porRefDireta.includes(nome)) {
      selecao[nome] = rotulosFiltrados(colecao, (item) =>
        mesmoNome(texto(item, 'workspace_ref'), workspaceName),
      );
      continue;
    }

    if (porRefMultipla.includes(nome)) {
      selecao[nome] = rotulosFiltrados(colecao, (item) => contemWorkspace(item, workspaceName));
      continue;
    }

    if (nome === 'price_table_items') continue; // tratado abaixo

    selecao[nome] = [];
  }

  // Itens das tabelas de preço vinculadas à área de trabalho
  const tabelas = pegar(colecoes, 'price_tables');
  const nomesTabelas = new Set(
    rotulosFiltrados(tabelas, (item) => contemWorkspace(item, workspaceName)).map((r) =>
      r.trim().toLowerCase(),
    ),
  );
  const itensTabela = pegar(colecoes, 'price_table_items');
  if (itensTabela) {
    selecao.price_table_items = rotulosFiltrados(itensTabela, (item) => {
      const ref = texto(item, 'price_table_ref');
      return typeof ref === 'string' && nomesTabelas.has(ref.trim().toLowerCase());
    });
  }

  return { crm: selecao };
}
